/** デバイス台帳の右パネル。インベントリの要約と「全画面で開く」。操作は詳細ページで。 */
import { Alert, Space } from "antd";

import type { DeviceApp } from "../../lib/api";
import { KeyValueList, PanelSection, SidePanel, TimeAgo } from "../../ui";
import { osText, policyProgress, policyStateTag, sourceTag, userCell } from "./deviceBits";
import { errorText, useDevice } from "./hooks";

/** パネルに出すアプリの数。全件は詳細ページのアプリタブ。 */
const PANEL_APPS = 8;

export function DevicePanel({ id, onClose }: { id: number; onClose: () => void }) {
  const query = useDevice(Number.isFinite(id) ? id : null);
  const d = query.data;
  const apps = (d?.inventory?.apps ?? []) as DeviceApp[];
  const policies = Object.entries(d?.policy_state ?? {});

  if (query.isError) {
    return (
      <SidePanel title="デバイス" onClose={onClose}>
        <Alert type="error" showIcon message="デバイスを取得できなかった" description={errorText(query.error)} />
      </SidePanel>
    );
  }

  return (
    <SidePanel
      title={d?.hostname ?? "デバイス"}
      loading={query.isLoading || !d}
      status={d && policyProgress(d)}
      sub={d && <div>{[d.model, osText(d)].filter(Boolean).join(" · ")}</div>}
      fullHref={`/mdm/devices/${id}`}
      onClose={onClose}
    >
      {d && (
        <>
          <PanelSection title="基本情報">
            <KeyValueList
              narrow
              items={[
                { label: "シリアル", value: d.serial, copyable: true },
                { label: "OS", value: osText(d) },
                { label: "機種", value: d.model },
                { label: "利用者", value: userCell(d) },
                { label: "取得元", value: sourceTag(d.source), always: true },
                { label: "最終チェックイン", value: d.last_seen ? <TimeAgo iso={d.last_seen} /> : "" },
              ]}
            />
          </PanelSection>
          <PanelSection title={`ポリシー適用(${policies.length})`}>
            {policies.length ? (
              <Space wrap size={4}>
                {policies.map(([k, v]) => policyStateTag(k, String(v)))}
              </Space>
            ) : (
              <div style={{ color: "var(--ux-text-disabled)" }}>なし</div>
            )}
          </PanelSection>
          <PanelSection title={`インストール済みアプリ(${apps.length} 件)`}>
            {apps.length ? (
              <div>
                {apps
                  .slice(0, PANEL_APPS)
                  .map((a) => a.name || "(名前なし)")
                  .join("、")}
                {apps.length > PANEL_APPS && (
                  <span style={{ color: "var(--ux-text-tertiary)" }}>
                    {" "}
                    ほか {apps.length - PANEL_APPS} 件(全画面で開くと全件)
                  </span>
                )}
              </div>
            ) : (
              <div style={{ color: "var(--ux-text-disabled)" }}>アプリ情報なし</div>
            )}
          </PanelSection>
        </>
      )}
    </SidePanel>
  );
}
